import { getApi } from '@/stores/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type PublishType = 'assortment' | 'launches'

type PublishStatusData = {
  publishType: PublishType
  status: 'pending' | 'processing' | 'completed' | 'failed'
  jobId: string
  initiatedBy: string
  initiatedByName: string | null
  initiatedAt: string
  completedAt?: string
  errorMessage?: string
  dynamoId?: string
}

type PublishStatusResponse = {
  isPublishing: boolean
  status: PublishStatusData | null
}

async function fetchPublishStatus(
  publishType: PublishType,
  signal?: AbortSignal,
): Promise<PublishStatusResponse> {
  const api = getApi()
  const res = await api.assortment.publish.status.$get(
    { query: { publishType } },
    { init: { signal } },
  )
  return res.json() as Promise<PublishStatusResponse>
}

export const PUBLISH_STATUS_KEY = 'publishStatus'

const PUBLISH_ERROR_KEYS: Record<number, string> = {
  409: 'publish.alreadyInProgress',
  423: 'publish.editsBlocked',
}

export function getPublishErrorDescription(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  publishType: string,
): string {
  const status = (error as { status?: number })?.status
  const key = status ? PUBLISH_ERROR_KEYS[status] : undefined
  return key ? t(key) : t('publish.failed', { publishType })
}

export function usePublishStatus(publishType: PublishType) {
  const query = useQuery({
    queryKey: [PUBLISH_STATUS_KEY, publishType],
    queryFn: ({ signal }) => fetchPublishStatus(publishType, signal),
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.isPublishing) return 3_000
      return false
    },
    refetchIntervalInBackground: true,
  })

  return {
    ...query,
    isPublishing: query.data?.isPublishing ?? false,
  }
}

/**
 * Same as usePublishStatus but also shows a toast when publish completes or fails.
 * Use this in exactly one component per page (e.g. PublishStatusBanner) to avoid duplicate toasts.
 */
const QUERIES_TO_INVALIDATE_ON_PUBLISH: Record<PublishType, string[]> = {
  assortment: ['assortment', 'lastUpdatedAssortementLog'],
  launches: ['launches', 'launchDetails'],
}

export function usePublishStatusWithNotification(publishType: PublishType) {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()
  const result = usePublishStatus(publishType)
  const previousStatusRef = useRef<string | null>(null)

  const currentStatus = result.data?.status?.status ?? null
  const errorMessage = result.data?.status?.errorMessage ?? null
  const initiatedByName = result.data?.status?.initiatedByName ?? null
  const publishTypeLabel = t(`publish.type.${publishType}`)

  useEffect(() => {
    const prevStatus = previousStatusRef.current

    if (prevStatus && (prevStatus === 'pending' || prevStatus === 'processing')) {
      const interpolation = { publishType: publishTypeLabel, name: initiatedByName ?? '' }

      if (currentStatus === 'completed') {
        toast.success(t('toast.success'), {
          description: initiatedByName
            ? t('publish.completedByUser', interpolation)
            : t('publish.completed', interpolation),
        })

        for (const queryKey of QUERIES_TO_INVALIDATE_ON_PUBLISH[publishType]) {
          queryClient.invalidateQueries({ queryKey: [queryKey] })
        }
      } else if (currentStatus === 'failed' && errorMessage !== 'Aborted by user') {
        toast.error(t('toast.error'), {
          description: initiatedByName
            ? t('publish.failedByUser', interpolation)
            : t('publish.failed', interpolation),
        })
      }
    }

    previousStatusRef.current = currentStatus
  }, [
    currentStatus,
    result.data?.status?.errorMessage,
    t,
    queryClient,
    publishType,
    publishTypeLabel,
    initiatedByName,
  ])

  return result
}

async function abortPublish(publishType: PublishType) {
  const api = getApi()
  const res = await api.assortment.publish.abort.$post({ json: { publishType } })
  if (!res.ok) throw new Error('Failed to abort')
  return res.json()
}

export function useAbortPublish(publishType: PublishType) {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => abortPublish(publishType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PUBLISH_STATUS_KEY, publishType] })
      toast.info(t('toast.info'), {
        description: t('publish.aborted', {
          publishType: t(`publish.type.${publishType}`),
        }),
      })
    },
    onError: () => {
      toast.error(t('toast.error'), { description: t('publish.abortFailed') })
    },
  })
}
